import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal } from 'antd';

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PageProps } from '@/types/PageProps';
import { AppPageHeader, DefaultRouteTabs } from '@/components/_common';

import {
  DirectSale,
  ListDirectSaleQuery,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import DirectSaleExtraFilters from '@/components/Movements/DirectSale/DirectSaleExtraFilters';
import { useDeleteDirectSale } from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import DirectSaleList from '@/components/Movements/DirectSale/DirectSaleList';
import Authorities from '@/types/Authority';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import usePageSearchHistory from '@/hooks/pageSearchHistory';

function DirectSalePage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListDirectSaleQuery>({
    order: 'DESC',
    ...convertParams<ListDirectSaleQuery>({
      search: 'string',
      status: 'array-string',
      createDate: 'string',
      companyId: 'number',
      customerName: 'string',
      code: 'string',
      manufacturerId: 'number',
      order: 'string',
      tagsIds: 'array-number',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const removeMutation = useDeleteDirectSale();

  const clearQueryParams = () => {
    setQueryParams({
      search: '',
      order: 'DESC',
      customerName: '',
      createDate: undefined,
      code: '',
      status: undefined,
      companyId: undefined,
    });
  };

  const handleRemoveClick = (directSale: DirectSale) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'directSale.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'directSale.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(directSale.id);
      },
      okType: 'danger',
    });
  };

  const newRoute = {
    ...route,
    routes: [
      route,
      ...route.routes.filter((route) => route.key === 'indicators'),
    ],
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <DefaultRouteTabs
        route={newRoute}
        persistSearchParams={false}
        basePath="/app/movements/directSale"
        permissions={route.permissions}
        extra={
          <Link to="/app/movements/directSale/create">
            <Button
              type="primary"
              block
              disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_ADD])}
            >
              <FormattedMessage id="directSale.create" />
            </Button>
          </Link>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({
            isShowingFilters,
          }: {
            // eslint-disable-next-line react/no-unused-prop-types
            isShowingFilters: boolean;
          }) =>
            isShowingFilters && (
              <Button type="primary" onClick={clearQueryParams}>
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <DirectSaleExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <DirectSaleList
          queryParams={queryDebounce}
          handleRemoveClick={handleRemoveClick}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default DirectSalePage;
